import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionLabel } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';

/**
 * Module 7 — A7.2 Notification settings, pixel-faithful to mockup
 * `module67-support.jsx` M7_Settings:
 *   - Quiet hours (Pause notifications + window)
 *   - By alert type (5 categories with mono code chips + toggles)
 *   - Channels (Push / In-app / Email digest)
 *
 * Phase-1 scope (decision P0-B): this is the visual surface + persisted prefs
 * (localStorage per user). Actual push DELIVERY (+ editable quiet-hours window)
 * is Phase 2 (R9) — surfaced honestly, not faked.
 *
 * Props: userId (for the localStorage key)
 */
const ALERT_TYPES = [
  { code: 'A2.3', key: 't14_risk',        label: 'T+14 risk',                 color: TCN.amber,   on: true },
  { code: 'A2.4', key: 't30_risk',        label: 'T+30 risk (high)',          color: TCN.red,     on: true },
  { code: 'A4.2', key: 'creator_alert',   label: 'Creator alert transition',  color: TCN.amber,   on: true },
  { code: 'A4.4', key: 'rankup_change',   label: 'Rank-up eligibility change',color: TCN.cyan,    on: true },
  { code: 'A5.6', key: 'morning_bonus',   label: 'Morning bonus-gap summary', color: TCN.magenta, on: true },
];
const CHANNELS = [
  { key: 'push', label: 'Push', on: true },
  { key: 'in_app', label: 'In-app', on: true },
  { key: 'email_digest', label: 'Email digest', on: false },
];

const Toggle = ({ on, onClick, w = 38, h = 22 }) => {
  const knob = h - 4;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onClick}
      style={{
        width: w, height: h, borderRadius: 99, border: 'none', cursor: 'pointer', padding: 0,
        background: on ? TCN.cyan : TCN.surface3, position: 'relative', transition: 'background .15s',
      }}
    >
      <span
        style={{
          position: 'absolute', top: 2, [on ? 'right' : 'left']: 2, width: knob, height: knob,
          borderRadius: 99, background: '#fff', transition: 'all .15s',
        }}
      />
    </button>
  );
};

const defaults = () => ({
  quiet_hours: true,
  types: Object.fromEntries(ALERT_TYPES.map((a) => [a.key, a.on])),
  channels: Object.fromEntries(CHANNELS.map((c) => [c.key, c.on])),
});

const NotificationSettings = ({ userId }) => {
  const { t } = useTranslation();
  const storageKey = `tcn_notif_settings_${userId || 'anon'}`;
  const [prefs, setPrefs] = useState(defaults);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = window.localStorage.getItem(storageKey);
    if (raw) {
      try { setPrefs({ ...defaults(), ...JSON.parse(raw) }); } catch { /* keep defaults */ }
    }
  }, [storageKey]);

  const persist = useCallback((next) => {
    setPrefs(next);
    if (typeof window !== 'undefined') window.localStorage.setItem(storageKey, JSON.stringify(next));
  }, [storageKey]);

  const toggleType = (k) => persist({ ...prefs, types: { ...prefs.types, [k]: !prefs.types[k] } });
  const toggleChannel = (k) => persist({ ...prefs, channels: { ...prefs.channels, [k]: !prefs.channels[k] } });

  const rowStyle = {
    padding: 12, background: TCN.surface1, border: `1px solid ${TCN.border}`,
    borderRadius: 12, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10,
  };

  return (
    <div>
      {/* Quiet hours — mockup lines 258-271 */}
      <SectionLabel>{t('tcn.m7.settings.quietHours', 'Quiet hours')}</SectionLabel>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: TCN.text }}>
              {t('tcn.m7.settings.pause', 'Pause notifications')}
            </div>
            <div style={{ fontSize: 12, color: TCN.textDim, marginTop: 4 }}>10:00 PM — 7:00 AM</div>
          </div>
          <Toggle w={44} h={26} on={prefs.quiet_hours} onClick={() => persist({ ...prefs, quiet_hours: !prefs.quiet_hours })} />
        </div>
      </Card>

      {/* By alert type — mockup lines 272-294 */}
      <SectionLabel>{t('tcn.m7.settings.byType', 'By alert type')}</SectionLabel>
      {ALERT_TYPES.map((a) => (
        <div key={a.key} style={rowStyle}>
          <span style={{ fontSize: 10, color: a.color, fontFamily: TCN.fontMono, fontWeight: 700, minWidth: 32 }}>
            {a.code}
          </span>
          <span style={{ flex: 1, fontSize: 13, color: TCN.text }}>
            {t(`tcn.m7.settings.type.${a.key}`, a.label)}
          </span>
          <Toggle on={!!prefs.types[a.key]} onClick={() => toggleType(a.key)} />
        </div>
      ))}

      {/* Channels — mockup lines 295-317 */}
      <SectionLabel>{t('tcn.m7.settings.channels', 'Channels')}</SectionLabel>
      {CHANNELS.map((c) => (
        <div key={c.key} style={{ ...rowStyle, gap: 0 }}>
          <span style={{ flex: 1, fontSize: 13, color: TCN.text }}>
            {t(`tcn.m7.settings.channel.${c.key}`, c.label)}
            {c.key === 'push' && (
              <span style={{ fontSize: 10, color: TCN.textMute, marginInlineStart: 8 }}>
                {t('tcn.m7.settings.soon', '· delivery soon')}
              </span>
            )}
          </span>
          <Toggle on={!!prefs.channels[c.key]} onClick={() => toggleChannel(c.key)} />
        </div>
      ))}
    </div>
  );
};

export default NotificationSettings;
